// {{WORKTREE_ROOT}}/picosponsorconsole/app/routes/signin._index.tsx
// PLAN2 WP10 — /signin. Regional hosts (app and Room): email → authStart (code) → pending challenge row → /signin/code;
// on the Room host codes and links only, the magic link returns to the Room host (R-129, D10-15).
// Global host (R-164): email → https://<rc>.app.<suffix>/signin?e=<sealed> for the only live region or the remembered
// ps_region choice, else /signin/region (the chooser) — the SAME answer for known and unknown addresses. The directory
// is read for every address (same work); WP36 (D36-5) adds the other-region hint email and the 900 ms floor when a second
// region is live: with two live regions every address sees the chooser, or goes to the remembered region.
import { json, redirect, type ActionFunctionArgs, type LoaderFunctionArgs } from "@remix-run/node";
import { Form, Link, useActionData, useLoaderData, useNavigation } from "@remix-run/react";
import { useEffect, useState } from "react";
import { Button, TextField } from "@picosponsor/design";
import { AuthFrame } from "~/components/auth/AuthFrame";
import { CsrfInput } from "~/components/common/CsrfInput";
import { useT } from "~/utils/locale";
import { failureText } from "~/utils/failureText";
import { TESTIDS } from "~/utils/testids";
import { assertHost } from "~/utils/host.server";
import { env } from "~/utils/env.server";
import { handleAction } from "~/utils/action.server";
import { failure, type ActionFailure } from "~/utils/actionResult";
import { authClient } from "~/utils/cognito.server";
import { ApiError } from "~/utils/apiCore.server";
import { getSessionView, saveChallenge } from "~/utils/session.server";
import { pageTitle } from "~/utils/pageTitle";
import { sanitizeNext } from "~/utils/next";
import { startSso } from "~/utils/ssoState.server";
import { sealEmail, unsealEmail } from "~/utils/seal.server";
import { lookupRegion } from "~/utils/logindir.server";
import { sendRegionHint, settleAfter, hintLang } from "~/utils/regionHint.server";
import { readPrefs, PREF_COOKIES } from "~/utils/prefs";
import { hostsFor } from "~/config/hosts";
import { LIVE_REGIONS } from "~/config/regions";
import { passkeysSupported, getAssertion } from "~/utils/webauthn.client";
import { useActionFetcher } from "~/hooks/useActionFetcher";

export const EMAIL_RE = /^[^@\s]{1,64}@[^@\s]{1,253}\.[a-z]{2,63}$/i;
const REASONS = ["signed-out", "onboarding-expired", "region", "session-expired"] as const;

export const meta = pageTitle("auth", "signin-title"); // PLAN3 C-3

export async function loader({ request }: LoaderFunctionArgs) {
  const host = assertHost(request, ["app", "room", "global"]);
  const e = env();
  const url = new URL(request.url);
  const reason = (REASONS as readonly string[]).includes(url.searchParams.get("reason") ?? "") ? url.searchParams.get("reason") : null;
  if (host === "global") {
    // ── WP36 ── with two live regions, name the remembered region and offer to forget it (D36-5)
    const live = LIVE_REGIONS.filter((r) => Boolean(e.consoleHosts[r] && e.loginSealSecrets[r]));
    const choice = readPrefs(request.headers.get("cookie")).regionChoice;
    const remembered = live.length > 1 && choice && live.includes(choice) ? choice : null;
    return json({ mode: "global" as const, next: "/", prefill: "", reason: null, region: null, otherRegionUrl: null, passkeys: false, remembered });
  }
  const next = sanitizeNext(url.searchParams.get("next"));
  const s = await getSessionView(request);
  if (s?.tid) throw redirect(next);
  const prefill = unsealEmail(url.searchParams.get("e"), e.siteSharedSecret, e.regionCode === "us" ? "handoff-us" : "handoff-eu") ?? "";
  const other = LIVE_REGIONS.find((r) => r !== e.regionCode);
  const otherRegionUrl = other ? `https://${hostsFor(e.stage, other, e.productDomain).console}/signin` : null;
  // Passkeys use the console host's RP id (WP05): offered on the app host only, never on the Room host (R-129, D10-15);
  // the Room host lands guests on /r and never links to the other region.
  return json({ mode: "regional" as const, next: host === "room" && next === "/" ? "/r" : next, prefill, reason, region: e.regionCode, otherRegionUrl: host === "room" ? null : otherRegionUrl, passkeys: host === "app", remembered: null });
}

export async function action({ request }: ActionFunctionArgs) {
  return handleAction(request, { session: "none" }, async ({ form }) => {
    const host = assertHost(request, ["app", "room", "global"]);
    // ── WP36 ── "Change region" on the global page forgets the remembered choice; the next sign-in shows the chooser.
    if (host === "global" && form.get("intent") === "forget-region") {
      return redirect("/signin", { headers: { "Set-Cookie": `${PREF_COOKIES.region}=; Path=/; Max-Age=0; SameSite=Lax; Secure` } });
    }
    const email = String(form.get("email") ?? "").trim().toLowerCase();
    if (!EMAIL_RE.test(email)) return json(failure("VALIDATION", "auth.error-email-invalid", undefined, { email: "auth.error-email-invalid" }), { status: 400 });
    if (host === "global") return discover(request, email);
    const e = env();
    const lang = readPrefs(request.headers.get("cookie")).lang ?? "en-US";
    // ── WP35 ── D35-14: a verified single sign-on domain that requires it goes straight to the identity provider.
    const sso = await authClient.ssoDiscover({ email, host: host === "room" ? "room" : "app" }).catch(() => ({ mode: "none" as const })); // PLAN-BEST-EFFORT: discovery down → the code flow still works; enforcement is server-side (D35-15)
    if (sso.mode === "required") return startSso(request, email, sanitizeNext(String(form.get("next") ?? "")));
    if (sso.mode === "blocked") return json(failure("FORBIDDEN_ROLE", "auth.sso-blocked"), { status: 403 });
    try {
      const r = await authClient.start({ email, lang, method: "code", ...(host === "room" ? { surface: "room" as const } : {}) }); // D10-15
      const headers = await saveChallenge(request, { email, challenge: r.challenge, method: "code", next: sanitizeNext(String(form.get("next") ?? "")) });
      return redirect("/signin/code", { headers });
    } catch (err) {
      if (err instanceof ApiError && err.code === "RATE_LIMITED") {
        const minutes = Math.max(1, Math.ceil(Number((err.details as { retryAfter?: number } | undefined)?.retryAfter ?? 60) / 60));
        return json(failure("RATE_LIMITED", "auth.error-rate-limited", { minutes }), { status: 429 });
      }
      if (err instanceof ApiError) console.warn(`signin: authStart refused on ${e.regionCode} (${err.code})`);
      throw err;
    }
  });
}

// ── WP36 ── D36-5 (C-51, §2.4 "never reveals existence"): with two live regions the answer never depends on whether the
// address is known. No remembered choice → everyone gets the chooser; a remembered choice → everyone goes there. The
// directory is still read for every address; when it names ANOTHER live region, that region emails the address a sign-in
// link (only the mailbox learns where the account lives). A fixed floor hides the extra call. One region → as in M0.
async function discover(request: Request, email: string): Promise<Response> {
  const started = Date.now();
  const e = env();
  const live = LIVE_REGIONS.filter((r) => Boolean(e.consoleHosts[r] && e.loginSealSecrets[r]));
  const prefs = readPrefs(request.headers.get("cookie"));
  const remembered = prefs.regionChoice && live.includes(prefs.regionChoice) ? prefs.regionChoice : null;
  // PLAN-BEST-EFFORT: a directory outage must look exactly like an unknown address (no oracle, no error page).
  const found = await lookupRegion(email).catch(() => null);
  const target = live.length === 1 ? live[0] : remembered;
  if (live.length > 1 && target && found && found !== target && live.includes(found)) await sendRegionHint(found, email, target, hintLang(prefs.lang));
  if (live.length > 1) await settleAfter(started);
  if (target) {
    const sealed = sealEmail(email, e.loginSealSecrets[target]!, target === "us" ? "handoff-us" : "handoff-eu");
    return redirect(`https://${e.consoleHosts[target]}/signin?e=${encodeURIComponent(sealed)}`);
  }
  const pending = sealEmail(email, e.csrfSecret, "pending");
  return redirect("/signin/region", { headers: { "Set-Cookie": `ps_ge=${pending}; Path=/signin; Max-Age=600; HttpOnly; Secure; SameSite=Lax` } });
}

export default function SignIn() {
  const data = useLoaderData<typeof loader>();
  const result = useActionData<ActionFailure>();
  const nav = useNavigation();
  const t = useT();
  const passkey = useActionFetcher<{ ok: boolean; options?: Record<string, unknown> } | ActionFailure>();
  const [canPasskey, setCanPasskey] = useState(false);
  const [email, setEmail] = useState(data.prefill);
  const [passkeyError, setPasskeyError] = useState<string | null>(null);
  useEffect(() => { setCanPasskey(data.passkeys && passkeysSupported()); }, [data.passkeys]);
  useEffect(() => {
    const d = passkey.data;
    if (!d || passkey.state !== "idle") return;
    if ("options" in d && d.options) {
      getAssertion(d.options)
        .then((credential) => passkey.submit({ intent: "finish", credential: JSON.stringify(credential) }, { action: "/signin/passkey" }))
        // PLAN-BEST-EFFORT: a cancelled/failed platform-authenticator prompt has nothing server-side to retry.
        .catch(() => setPasskeyError(t("auth", "error-passkey-failed")));
    } else if ("ok" in d && d.ok === false && "error" in d) setPasskeyError(failureText(t, d.error));
  }, [passkey.data, passkey.state]); // eslint-disable-line react-hooks/exhaustive-deps

  const busy = nav.state !== "idle" && nav.formData?.get("intent") === "start";
  const regionLine = data.region ? t("auth", "region-line", { region: t("common", `region-${data.region}`) }) : null;
  const error = result && result.ok === false ? failureText(t, result.error) : passkeyError;
  return (
    <AuthFrame regionLine={regionLine} testId={data.mode === "global" ? TESTIDS.auth.global.page : TESTIDS.auth.signin.page}>
      <h1 className="display m-0 text-30 leading-0.95 tracking-tight-30 cq-md:text-32">{t("auth", "signin-title")}</h1>
      {data.reason ? <p role="status" className="m-0 text-14 text-dim" data-testid={TESTIDS.auth.signin.reason}>{t("auth", `reason-${data.reason}`)}</p> : null}
      <p className="m-0 text-14 text-dim">{t("auth", data.mode === "global" ? "global-lede" : "signin-lede")}</p>
      <Form method="post" className="flex flex-col gap-4">
        <CsrfInput />
        <input type="hidden" name="next" value={data.next} />
        <TextField
          // eslint-disable-next-line jsx-a11y/no-autofocus -- intentional: sign-in is the page's only task, and prefilled email skips it
          label={t("auth", "email-label")} hideLabel name="email" type="email" inputMode="email" required autoFocus={!data.prefill}
          autoComplete="username webauthn" placeholder={t("placeholders", "email")} defaultValue={data.prefill}
          onChange={(ev: React.ChangeEvent<HTMLInputElement>) => setEmail(ev.target.value)}
          error={error ?? undefined} data-testid={TESTIDS.auth.signin.email}
        />
        <Button type="submit" name="intent" value="start" variant="primary" size="l" className="w-full" loading={busy} data-testid={TESTIDS.auth.signin.submit}>
          {t("auth", "continue")}
        </Button>
        {canPasskey ? (
          <Button type="button" variant="secondary" size="l" className="w-full" loading={passkey.state !== "idle"}
            onClick={() => { setPasskeyError(null); if (EMAIL_RE.test(email)) passkey.submit({ intent: "start", email }, { action: "/signin/passkey" }); else setPasskeyError(t("auth", "error-email-invalid")); }}
            data-testid={TESTIDS.auth.signin.passkey}>
            {t("auth", "passkey-signin")}
          </Button>
        ) : null}
      </Form>
      {data.mode === "regional" ? (
        <Link to={`/signin/sso${data.next && data.next !== "/" ? `?next=${encodeURIComponent(data.next)}` : ""}`} className="text-14 font-bold text-accent" data-testid={TESTIDS.auth.sso.link}>{t("auth", "sso-link")}</Link>
      ) : null}
      {data.mode === "global" && data.remembered ? (
        // ── WP36 ── the remembered region, and a way to forget it (D36-5)
        <Form method="post" className="flex flex-wrap items-center gap-2 text-14 text-dim" data-testid={TESTIDS.regionChooser.remembered}>
          <CsrfInput />
          <span>{t("auth", "region-remembered", { region: t("common", `region-${data.remembered}`) })}</span>
          <Button type="submit" name="intent" value="forget-region" variant="quiet" data-testid={TESTIDS.regionChooser.change}>
            {t("auth", "region-change")}
          </Button>
        </Form>
      ) : null}
      {data.otherRegionUrl ? (
        // PLAN-ANCHOR-OK: the other region's console is a different host (external)
        <a href={data.otherRegionUrl} className="text-14 font-bold text-accent" data-testid={TESTIDS.auth.signin.otherRegion}>
          {t("auth", "other-region", { region: t("common", `region-${data.region === "us" ? "eu" : "us"}`) })}
        </a>
      ) : null}
    </AuthFrame>
  );
}
